import React, { useState, useEffect, memo } from 'react';
import { Card, Space, DatePicker, Button, Spin, Select, Statistic, Row, Col, Tag, message } from 'antd';
import { MetricCardHeader } from './Shared/MetricCardHeader';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import { 
  ClockCircleOutlined, 
  ThunderboltOutlined, 
  ReloadOutlined,
  DownloadOutlined,
  FireOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { getHourlyActivityHeatmap } from '../../api/api';
import type { HourlyActivityResponse } from '../../types/api-models';

const { RangePicker } = DatePicker;

interface HourlyActivityHeatmapProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const HourlyActivityHeatmap: React.FC<HourlyActivityHeatmapProps> = memo(({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<HourlyActivityResponse | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);



  useEffect(() => {
    if (!isExpanded && dashboardData?.hourly_activity) {
      setData(dashboardData.hourly_activity);
    }
  }, [isExpanded, dashboardData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getHourlyActivityHeatmap(
        'daily',
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );
      setData(response);
    } catch (error) {
      console.error('Error fetching hourly activity heatmap:', error);
      message.error('Failed to load hourly activity heatmap');
    } finally {
      setLoading(false);
    }
  };

  const getActivityColor = (activity: number, maxActivity: number) => {
    const intensity = activity / maxActivity;
    if (intensity > 0.8) return '#ff4d4f';
    if (intensity > 0.6) return '#ff7a45';
    if (intensity > 0.4) return '#faad14';
    if (intensity > 0.2) return '#52c41a';
    return '#f0f0f0';
  };


  // Helper: Render statistics row
  const renderStatistics = () => {
    if (!data) return null;

    const { hourlyActivity, peakHours } = data;
    const totalMessages = hourlyActivity.reduce((sum, h) => sum + h.messageCount, 0);
    const avgResponseTime = Math.round(
      hourlyActivity.reduce((sum, h) => sum + h.avgResponseTime, 0) / hourlyActivity.length
    );

    return (
      <div style={{ marginBottom: isExpanded ? 24 : 16 }}>
        {!isExpanded && (
          <div style={{ marginBottom: 16 }}>
            <MetricCardHeader
              title="Total Messages"
              value={totalMessages}
              trend={data.peak_hour_trend}
              subtitle="All messages processed"
            />
          </div>
        )}
        
        <Row gutter={[16, 16]}>
          <Col span={isExpanded ? 6 : 12}>
            <Statistic
              title="Peak Hours"
              value={peakHours.length}
              suffix="hours"
              prefix={<FireOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
          <Col span={isExpanded ? 6 : 12}>
            <Statistic
              title="Busiest Hour"
              value={peakHours[0] !== undefined ? peakHours[0] : '-'}
              suffix={peakHours[0] !== undefined ? ":00" : ""}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          {isExpanded && (
            <>
              <Col span={6}>
                <Statistic
                  title="Total Messages"
                  value={totalMessages}
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Avg Response Time"
                  value={avgResponseTime}
                  suffix="ms"
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
            </>
          )}
        </Row>
      </div>
    );
  };

  // Helper: Render heatmap chart
  const renderHeatmapChart = () => {
    if (!data) return null;

    const { hourlyActivity, peakHours } = data;
    const maxActivity = Math.max(...hourlyActivity.map(h => h.messageCount));

    return (
      <Row gutter={[16, 16]}>
        <Col span={isExpanded ? 16 : 24}>
          <div style={{ height: isExpanded ? 300 : 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyActivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="hour" 
                  tickFormatter={(value) => `${value}:00`}
                />
                <YAxis label={isExpanded ? { value: 'Messages', angle: -90, position: 'insideLeft' } : undefined} />
                <Tooltip 
                  formatter={(value) => [value, 'Messages']}
                  labelFormatter={(label) => `Hour: ${label}:00`}
                />
                <Bar dataKey="messageCount">
                  {hourlyActivity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getActivityColor(entry.messageCount, maxActivity)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Col>
        {isExpanded && (
          <Col span={8}>
            <div style={{ padding: '20px 0' }}>
              <h4>Peak Hours</h4>
              <div style={{ marginBottom: 16 }}>
                {peakHours.slice(0, 5).map((hour, index) => (
                  <Tag key={index} color={index === 0 ? 'red' : index === 1 ? 'orange' : 'blue'} style={{ marginBottom: 8 }}>
                    {hour}:00
                  </Tag>
                ))}
              </div>
              <div style={{ marginTop: 20 }}>
                <h4>Activity Intensity</h4>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ width: 20, height: 20, backgroundColor: '#f0f0f0', marginRight: 8 }}></div>
                  <span>Low (0-20%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ width: 20, height: 20, backgroundColor: '#52c41a', marginRight: 8 }}></div>
                  <span>Medium (20-40%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ width: 20, height: 20, backgroundColor: '#faad14', marginRight: 8 }}></div>
                  <span>High (40-60%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ width: 20, height: 20, backgroundColor: '#ff7a45', marginRight: 8 }}></div>
                  <span>Very High (60-80%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: 20, height: 20, backgroundColor: '#ff4d4f', marginRight: 8 }}></div>
                  <span>Peak (80-100%)</span>
                </div>
              </div>
            </div>
          </Col>
        )}
      </Row>
    );
  };

  const renderCollapsedView = () => {
    if (!data) {
      return <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>No data available</div>;
    }

    return (
      <div style={{ height: '100%' }}>
        {renderStatistics()}
        {renderHeatmapChart()}
      </div>
    );
  };

  const renderExpandedView = () => {
    return (
      <div style={{ height: '100%' }}>
        {/* Controls Row */}
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space wrap>
            <span style={{ fontSize: 13, color: '#666' }}>Date Range:</span>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              format="YYYY-MM-DD"
              style={{ width: 260 }}
              disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
            />
            <span style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
              Showing hourly pattern averaged across selected days
            </span>
          </Space>
          
          <Space>
            <Button type="primary" onClick={fetchData} loading={loading}>
              Refresh
            </Button>
          </Space>
        </Space>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" tip="Loading hourly activity heatmap..." />
          </div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view hourly activity heatmap</p>
            <p style={{ fontSize: 14 }}>Select a period and click "Apply Filters" to load data</p>
          </div>
        ) : (
          <>
            {renderStatistics()}
            <Card title="24-Hour Activity Heatmap">
              {renderHeatmapChart()}
            </Card>
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%' }}>
      {isExpanded ? renderExpandedView() : renderCollapsedView()}
    </div>
  );
});

HourlyActivityHeatmap.displayName = 'HourlyActivityHeatmap';

export default HourlyActivityHeatmap;
